import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Card } from '@safetyhub/ui';
import { InspectionListItem } from '../../../lib/inspections';
import { buildDaySummaryMap } from '../lib/daySummary';
import {
  weekDates,
  nearestRowScrollTop,
  monthParityOfDateKey,
} from '../lib/calendarView';
import CalendarDayCell from './CalendarDayCell';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 주 행 높이(px) — 스냅/가상화 계산과 CSS가 공유하는 정본 */
const ROW_HEIGHT = 100;
/** 뷰포트에 보이는 주 수 (월간 6주 그리드와 같은 밀도) */
const VISIBLE_ROWS = 6;
/** DOM에 렌더하는 주 수 (가시 6주 + 전후 버퍼) */
const WINDOW_SIZE = 21;
/** 뷰포트 상단 행이 이 여유보다 가장자리에 가까워지면 윈도우를 7주 이동 */
const EDGE_ROWS = 3;
/** 중심 주 = 가시 6행 중 3번째 행 (topWeek + CENTER_ROW_OFFSET) */
const CENTER_ROW_OFFSET = Math.floor(VISIBLE_ROWS / 2) - 1; // = 2
/** 초기/재앵커 시 중심 주가 윈도우 한가운데(index 10) → 상단 행 = 10 - 2 = 8 */
const ANCHOR_TOP_ROW = Math.floor(WINDOW_SIZE / 2) - CENTER_ROW_OFFSET;
/** 스크롤 정지 판정 후 주 행 스냅까지의 대기(ms) */
const SNAP_IDLE_MS = 160;

export interface WeekStreamCalendarHandle {
  /** 주 단위 상대 이동 (Chevron 버튼) */
  scrollByWeeks: (delta: number) => void;
  /** 특정 주로 이동 ("오늘" 버튼 등) — 윈도우 밖이면 즉시 재앵커 */
  scrollToWeek: (weekIndex: number) => void;
}

interface WeekStreamCalendarProps {
  /** 마운트 시 뷰포트 중심에 둘 주 인덱스 */
  initialWeek: number;
  inspections: InspectionListItem[];
  selectedDate: string | null;
  onDateClick: (date: string | null) => void;
  /** 뷰포트 중심 주 변경 통지 (헤더 월 제목·fetch 범위 갱신용) */
  onCenterWeekChange?: (weekIndex: number) => void;
}

/**
 * 주간 뷰: 연속 주 스트림. 네이티브 세로 스크롤 + 정지 시 주 행 스냅.
 * 가시 주 ±버퍼만 DOM 렌더 — 가장자리 접근 시 startWeek을 7주 이동시키고
 * scrollTop을 같은 픽셀만큼 보정해 화면 위치를 유지한다(무한 스트림).
 * 월은 표시로 강등: 셀 배경 톤을 달 패리티로 교차 + 매월 1일 셀에 "N월" 라벨.
 */
const WeekStreamCalendar = forwardRef<WeekStreamCalendarHandle, WeekStreamCalendarProps>(
  function WeekStreamCalendar(
    { initialWeek, inspections, selectedDate, onDateClick, onCenterWeekChange },
    ref,
  ) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [startWeek, setStartWeek] = useState(() => initialWeek - Math.floor(WINDOW_SIZE / 2));
    const startWeekRef = useRef(startWeek);
    startWeekRef.current = startWeek;

    const lastCenterRef = useRef<number | null>(null);
    const snapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragRef = useRef<{ startY: number; startScrollTop: number; moved: boolean } | null>(null);
    const suppressClickRef = useRef(false);

    const todayKey = useMemo(() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    const dayMap = useMemo(() => buildDaySummaryMap(inspections), [inspections]);

    // 초기 위치: initialWeek이 뷰포트 중심(상단에서 3번째 행)에 오도록
    useLayoutEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTop = ANCHOR_TOP_ROW * ROW_HEIGHT;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 초기 중심 주 통지 (헤더 제목 동기화)
    useEffect(() => {
      lastCenterRef.current = initialWeek;
      onCenterWeekChange?.(initialWeek);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => {
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    }, []);

    // 반응형 분기(sm 미만)에서 숨겨진 채 마운트되면 scrollTop 설정이 무시됨 —
    // 표시 전환 시 scrollTop이 0으로 남아 있으면 윈도우 중앙으로 재앵커
    useEffect(() => {
      const el = scrollRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => {
        if (el.clientHeight > 0 && el.scrollTop === 0) {
          el.scrollTop = ANCHOR_TOP_ROW * ROW_HEIGHT;
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const reportCenter = (el: HTMLDivElement) => {
      const centerWeek =
        startWeekRef.current + Math.round(el.scrollTop / ROW_HEIGHT) + CENTER_ROW_OFFSET;
      if (centerWeek !== lastCenterRef.current) {
        lastCenterRef.current = centerWeek;
        onCenterWeekChange?.(centerWeek);
      }
    };

    const snapToNearestRow = () => {
      const el = scrollRef.current;
      if (!el || dragRef.current) return;
      const target = nearestRowScrollTop(el.scrollTop, ROW_HEIGHT);
      if (Math.abs(el.scrollTop - target) > 1) {
        el.scrollTo({ top: target, behavior: 'smooth' });
      }
    };

    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el || el.clientHeight === 0) return; // 반응형 분기에서 숨겨진 경우 무시

      // 가장자리 접근 시 윈도우 이동 + scrollTop 보정 (화면 위치 유지).
      // flushSync로 행 재배치를 paint 전에 완료시켜 scrollTop 보정과 원자적으로 묶는다.
      const topRow = el.scrollTop / ROW_HEIGHT;
      if (topRow < EDGE_ROWS) {
        startWeekRef.current -= 7;
        flushSync(() => setStartWeek(startWeekRef.current));
        el.scrollTop += 7 * ROW_HEIGHT;
      } else if (topRow > WINDOW_SIZE - VISIBLE_ROWS - EDGE_ROWS) {
        startWeekRef.current += 7;
        flushSync(() => setStartWeek(startWeekRef.current));
        el.scrollTop -= 7 * ROW_HEIGHT;
      }

      reportCenter(el);

      // 정지 후 가장 가까운 주 행에 스냅 (드래그 중에는 release 시점에 스냅)
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
      snapTimerRef.current = setTimeout(snapToNearestRow, SNAP_IDLE_MS);
    };

    // 마우스 드래그 패닝 (터치는 네이티브 스크롤 사용)
    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const el = scrollRef.current;
      if (!el) return;
      dragRef.current = { startY: e.clientY, startScrollTop: el.scrollTop, moved: false };
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      const el = scrollRef.current;
      if (!drag || !el) return;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.abs(dy) > 4) {
        drag.moved = true;
        // 드래그로 확정된 뒤에만 capture — 즉시 capture하면 click이 컨테이너로
        // 재타게팅되어 셀 클릭(날짜 선택)이 막힌다
        el.setPointerCapture(e.pointerId);
      }
      el.scrollTop = drag.startScrollTop - dy;
    };

    const handlePointerEnd = () => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      if (drag.moved) {
        suppressClickRef.current = true;
        snapToNearestRow();
      }
    };

    // 드래그 직후 셀 클릭(날짜 선택) 억제
    const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    useImperativeHandle(ref, () => ({
      scrollByWeeks: (delta: number) => {
        const el = scrollRef.current;
        if (!el) return;
        const target = nearestRowScrollTop(el.scrollTop, ROW_HEIGHT) + delta * ROW_HEIGHT;
        el.scrollTo({ top: target, behavior: 'smooth' });
      },
      scrollToWeek: (weekIndex: number) => {
        const el = scrollRef.current;
        if (!el) return;
        const rowInWindow = weekIndex - CENTER_ROW_OFFSET - startWeekRef.current;
        // 윈도우 안이면 부드럽게, 밖이면 재앵커 후 즉시 배치
        if (rowInWindow >= 0 && rowInWindow <= WINDOW_SIZE - VISIBLE_ROWS) {
          el.scrollTo({ top: rowInWindow * ROW_HEIGHT, behavior: 'smooth' });
        } else {
          startWeekRef.current = weekIndex - Math.floor(WINDOW_SIZE / 2);
          setStartWeek(startWeekRef.current);
          el.scrollTop = ANCHOR_TOP_ROW * ROW_HEIGHT;
          reportCenter(el);
        }
      },
    }));

    // 렌더할 주 목록 (가상화 윈도우)
    const weeks = useMemo(
      () => Array.from({ length: WINDOW_SIZE }, (_, i) => startWeek + i),
      [startWeek],
    );

    return (
      <Card radius="lg" padding="none" className="overflow-hidden">
        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 border-b border-line bg-surface-soft">
          {WEEKDAYS.map((day, i) => (
            <div
              key={day}
              className={`py-2.5 text-center text-xs font-bold tracking-wider ${
                i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-ink-500'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* 주 스트림 (세로 스크롤 + 주 행 스냅) */}
        <div
          ref={scrollRef}
          // [overflow-anchor:none] 필수 — 윈도우 시프트(행 재배치+scrollTop 보정) 시 브라우저
          // 스크롤 앵커링이 화면 유지를 위해 또 한 번 보정해 이중 적용(-1400px→0 클램프→역시프트
          // 진동으로 원위치 복귀)되던 실사고(2026-07-27, "8월 갔다가 7월로 돌아감"). 가상 스크롤러 관례.
          className="scrollbar-hide overflow-y-auto overscroll-contain select-none cursor-grab active:cursor-grabbing [overflow-anchor:none]"
          style={{ height: VISIBLE_ROWS * ROW_HEIGHT }}
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onClickCapture={handleClickCapture}
        >
          {weeks.map((weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7" style={{ height: ROW_HEIGHT }}>
              {weekDates(weekIndex).map((dateKey, dow) => {
                const dayNum = parseInt(dateKey.split('-')[2], 10);
                const monthNum = parseInt(dateKey.split('-')[1], 10);
                const isSelected = dateKey === selectedDate;
                return (
                  <CalendarDayCell
                    key={dateKey}
                    dateKey={dateKey}
                    isCurrentMonth
                    summary={dayMap.get(dateKey)}
                    isToday={dateKey === todayKey}
                    isSelected={isSelected}
                    dow={dow}
                    onClick={(e) => {
                      (e.currentTarget as HTMLButtonElement).blur();
                      if (!isSelected) onDateClick(dateKey);
                    }}
                    sizeClass="h-full overflow-hidden"
                    toneClass={monthParityOfDateKey(dateKey) === 0 ? 'bg-white' : 'bg-surface-soft'}
                    monthLabel={dayNum === 1 ? `${monthNum}월` : null}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </Card>
    );
  },
);

export default WeekStreamCalendar;
